import { initializeSocket, disconnectSocket } from "../endpoints/socket";
import {
  fetchJobMessagesProfile,
  fetchUnseenMessageCount,
  incrementRoomUnread,
  setJobMembersProfile,
} from "../store/actions/message.action";
import store from "../store/index";
import { playNotificationSound } from "../utils/helperFunction";
import { updateAndSortChatGroups } from "../utils/updateAndSortChatGroups";
import { useSnackbar } from "notistack";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { subscribeUnreadCount } from "../utils/subscribeUnreadCount";

const SocketContext = createContext<any>(null);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [socket, setSocket] = useState<any>(null);
  // Preserve the public .current interface while notifying consumers on creation.
  const socketContext = useMemo(() => ({ current: socket }), [socket]);
  const { enqueueSnackbar } = useSnackbar();
  const { logged_in, user, token } = useSelector((state: any) => state.auth);

  useEffect(() => {
    if (logged_in && user) {
      const sessionId = localStorage.getItem("sessionId");
      if (!sessionId) {
        console.error("❌ SocketProvider: No sessionId found");
        return;
      }

      // Initialize socket
      const socket = initializeSocket();
      if (!socket) return;
      setSocket(socket);
      const stopUnreadCountSync = subscribeUnreadCount(socket, () => {
        store.dispatch(fetchUnseenMessageCount());
      });

      // Add detailed event listeners
      socket?.on("connect", () => {
        store.dispatch(fetchJobMessagesProfile());
        console.log(
          "✅ SocketProvider: Socket connected successfully",
          socket.id
        );
      });

      socket?.on("disconnect", (reason) => {
        console.log("🔌 SocketProvider: Socket disconnected", reason);
      });

      socket?.on("connect_error", (err) => {
        console.error("❌ SocketProvider: Connection error", err.message);
      });

      // `newMessage` is the notification event — toast + sound only. Count and
      // ordering updates are handled optimistically by RECEIVE_MESSAGE below.
      socket?.on("newMessage", (message) => {
        const currentRoute = window.location.pathname;
        if (!currentRoute?.includes("messages")) {
          playNotificationSound();
          enqueueSnackbar(`Message : ${message?.message}`, {
            variant: "success",
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
          });
        }
      });

      // Keep inbox updates independent of whichever chat screen is mounted.
      const handleAnyEvent = (event: string, ...args: any[]) => {
        if (event === "pong") return; // Filter out pong to reduce noise

        if (event === "job:chat:receive") {
          const message = args?.[0];
          // Reorder + refresh last message for the affected room.
          const updatedChatGroups = updateAndSortChatGroups(
            store.getState().messageReducer?.membersProfile,
            message
          );
          if (updatedChatGroups?.length) {
            store.dispatch(setJobMembersProfile(updatedChatGroups));
          } else {
            // Room not in the current list (e.g. brand-new conversation) — reconcile.
            store.dispatch(fetchJobMessagesProfile());
          }

          // Optimistically bump the unread badge unless the message is ours or
          // the room is already open on screen (useChat marks those seen).
          const selfIds = [user?._id, user?.userId].filter(Boolean);
          const isOwnMessage = selfIds.includes(message?.senderId);
          const activeChat = store.getState().messageReducer?.chatReceiverDetails;
          const isViewingRoom =
            activeChat?.jobId === message?.jobId && activeChat?.participantId === message?.senderId;
          if (!isOwnMessage && !isViewingRoom) {
            store.dispatch(
              incrementRoomUnread({ jobId: message?.jobId, userId: user?._id, participantId: message?.senderId })
            );
          }
        }
        // console.log(`📨 SocketProvider: Received event [${event}]`, args?.[0]);
      };
      socket?.onAny(handleAnyEvent);

      const resumeConnection = () => {
        if (!socket.connected) socket.connect();
      };
      window.addEventListener("focus", resumeConnection);

      return () => {
        stopUnreadCountSync();
        window.removeEventListener("focus", resumeConnection);
        socket.offAny(handleAnyEvent);
        disconnectSocket();
        setSocket(null);
      };
    } else {
      console.log(
        "🚫 SocketProvider: User not logged in, disconnecting socket"
      );
      disconnectSocket();
      setSocket(null);
    }
  }, [logged_in, user?._id]); // Re-run when user ID changes

  // Authentication failures do not trigger Socket.IO's automatic reconnect.
  // A freshly refreshed login token must resume a disconnected connection.
  useEffect(() => {
    if (socket && token && !socket.connected) socket.connect();
  }, [socket, token]);

  return (
    <SocketContext.Provider value={socketContext}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
